"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, Phone, Mail, Calendar as CalendarIcon, Loader, CircleCheck, Map } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Հարցումն ընդունված է</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Նոր հարցում
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="firstName" rules={{
        required: "Անունը պարտադիր է",
        minLength: {
          value: 2,
          message: "Նվազագույնը 2 նիշ"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Անուն</FormLabel>
              <FormControl>
                <Input placeholder="Ձեր անունը" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="lastName" rules={{
        required: "Ազգանունը պարտադիր է"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Ազգանուն</FormLabel>
              <FormControl>
                <Input placeholder="Ձեր ազգանունը" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <FormField control={control} name="email" rules={{
        required: "Էլ. փոստը պարտադիր է",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Անվավեր էլ. փոստի հասցե"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Էլ. փոստ</FormLabel>
              <FormControl>
                <Input type="email" placeholder="orinak@mail.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Հեռախոսահամարը պարտադիր է"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Հեռախոսահամար</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+374 00 000000" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <FormField control={control} name="projectType" rules={{
        required: "Խնդրում ենք ընտրել նախագծի տեսակը"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Նախագծի տեսակ</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Ընտրեք ոլորտը" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="satellite">Արբանյակային պատկերների ստացում</SelectItem>
                  <SelectItem value="photogrammetry">Ֆոտոգրամետրիական աշխատանքներ</SelectItem>
                  <SelectItem value="geodetic">Գեոդեզիական աշխատանքներ</SelectItem>
                  <SelectItem value="digital_maps">Թվային քարտեզների ստեղծում</SelectItem>
                  <SelectItem value="other">Այլ</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="consultationDate" render={({
        field
      }) => <FormItem className="flex flex-col justify-end">
              <FormLabel>Նախընտրելի ամսաթիվ (Խորհրդատվություն)</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant={"outline"} className={cn("w-full pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                      {field.value ? new Date(field.value).toLocaleDateString('hy-AM') : <span>Ընտրեք ամսաթիվ</span>}
                      <SafeHydrate><CalendarIcon className="ml-auto h-4 w-4 opacity-50" /></SafeHydrate>
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={date => field.onChange(date?.toISOString())} disabled={date => date < new Date() || date < new Date("1900-01-01")} initialFocus /></SafeHydrate>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="mt-6">
        <FormField control={control} name="details" rules={{
        required: "Խնդրում ենք նկարագրել ձեր նախագիծը",
        minLength: {
          value: 10,
          message: "Նկարագրությունը պետք է պարունակի առնվազն 10 նիշ"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Նախագծի մանրամասներ</FormLabel>
              <FormControl>
                <Textarea placeholder="Նկարագրեք ձեր նախագծի նպատակները, պահանջվող ճշգրտությունը և այլ կարևոր մանրամասներ..." className="min-h-[120px] resize-y" {...field} />
              </FormControl>
              <FormDescription>
                Որքան շատ մանրամասներ տրամադրեք, այնքան ավելի արդյունավետ կլինի մեր խորհրդատվությունը:
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full mt-8" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Ուղարկվում է...
          </> : 'Ամրագրել Խորհրդատվություն'}
      </Button>
    </Form>;
}
export default function ContactSection() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return;
}